import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Link from '@atlaskit/link';
import { Card, CardAction } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';

import { useIntl } from '@atlassian/jira-intl';
import type { DefaultAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { matchDefaultAttributeSubType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/utils/type-matchers/index.tsx';
import messages from '../../common/messages.tsx';
import { ReadViewContainer } from '../../common/ui/styled.tsx';

const renderUrlAnchor = (url: string) =>
	fg('replace-anchor-with-link__falcons') ? (
		<Link target="_blank" rel="noopener noreferrer" href={url}>
			{url}
		</Link>
	) : (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
		<a target="_blank" rel="noopener noreferrer" href={url}>
			{url}
		</a>
	);

const renderEmailReadView = (emails: string[]) => (
	<MultiTextContainer data-testid="servicedesk-insight-attribute-views.ui.text.multi-text-container">
		{emails.map((email, index) => (
			<span key={index}>
				{fg('replace-anchor-with-link__falcons') ? (
					<Link href={`mailto:${email}`}>{email}</Link>
				) : (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a href={`mailto:${email}`}>{email}</a>
				)}
			</span>
		))}
	</MultiTextContainer>
);

export type TextReadViewProps = {
	value: string | string[] | null;
	defaultAttributeType: DefaultAttributeType;
	showPlaceholderIfEmpty?: boolean;
};
export const TextReadView = ({
	value,
	defaultAttributeType,
	showPlaceholderIfEmpty = true,
}: TextReadViewProps): React.JSX.Element => {
	const renderSmartLink = (url: string) => {
		const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
			event.preventDefault();
			// Open smart link in new tab by default, as it takes the user out of Jira
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.open(url, '_blank', 'noopener');
		};

		return (
			<Card
				url={url}
				appearance="inline"
				fallbackComponent={() => renderUrlAnchor(url)}
				onClick={handleClick}
				actionOptions={{
					hide: false,
					exclude: [CardAction.ChangeStatusAction],
				}}
			/>
		);
	};

	const renderUrlReadViewWithSmartLinks = (urls: string[]) => (
		<MultiTextContainer data-testid="servicedesk-insight-attribute-views.ui.text.multi-text-container">
			{urls.map((url, index) => (
				<span key={index}>{renderSmartLink(url)}</span>
			))}
		</MultiTextContainer>
	);

	const { formatMessage } = useIntl();
	let readViewContent;

	const getValueAsArray = (v: string | string[] | null): string[] => {
		let unfilteredArray;
		if (typeof v === 'string') {
			unfilteredArray = [v];
		} else {
			unfilteredArray = v ?? [];
		}

		return unfilteredArray.filter((val) => val != null && val !== '');
	};

	const valuesAsArray = getValueAsArray(value);

	const hasValue = valuesAsArray.length > 0;

	if (hasValue) {
		const firstValue = valuesAsArray[0];
		const renderValue = (): ReactNode => firstValue;
		readViewContent = matchDefaultAttributeSubType(defaultAttributeType.defaultType, {
			url: () => renderUrlReadViewWithSmartLinks(valuesAsArray),
			email: () => renderEmailReadView(valuesAsArray),
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			float: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			integer: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			ipAddress: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			text: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			textarea: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			select: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			boolean: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			date: renderValue,
			// @ts-expect-error - TS2322 - Type '() => ReactNode' is not assignable to type '() => Element | null | undefined'.
			datetime: renderValue,
		});
	} else {
		readViewContent = showPlaceholderIfEmpty
			? formatMessage(messages.placeholderNoneEntered)
			: null;
	}
	return (
		<ReadViewContainer isPlaceholder={!hasValue}>
			{readViewContent}
			{hasValue && defaultAttributeType.suffix !== undefined
				? ` ${defaultAttributeType.suffix}`
				: null}
		</ReadViewContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MultiTextContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginRight: token('space.100'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
			lineHeight: '24px',
			display: 'inline-block',
		},
	},
});
